import { CampaignType, DspBlacklistTypeEnum } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { DspStrategyService } from "@front/m19-services";
import { DspCreateStrategyComponent } from "@m19-board/dsp/dsp-create-strategy/dsp-create-strategy.component";
import { DspManagerComponent } from "@m19-board/dsp/dsp-manager/dsp-manager.component";
import { getStrategyPageRoutes } from "@m19-board/strategies/strategy-page/strategy-page.routes";
import { M19Route } from "@m19-board/utils/M19Route";

import { DspStatsComponent } from "@m19-board/dsp/dsp-stats/dsp-stats.component";
import { dspStrategyPageResolver } from "@m19-board/strategies/strategy-page/strategy-page.resolver";
import { DspAudiencesPageComponent } from "@m19-board/dsp/dsp-audiences-page/dsp-audiences-page.component";
import { DspCreateAudiencePageComponent } from "@m19-board/dsp/dsp-create-audience-page/dsp-create-audience-page.component";
import { DspLayoutComponent } from "@m19-board/dsp/dsp-layout.component";
import { canCreateDspStrategyGuard } from "@m19-board/guards/dsp-guard/can-create-dsp-strategy.guard";
import { DspBlacklistPageComponent } from "@m19-board/dsp/dsp-blacklist/dsp-blacklist-page.component";
import { DspBlacklistComponent } from "@m19-board/dsp/dsp-blacklist/dsp-blacklist/dsp-blacklist.component";
import { dspBlacklistSaveGuard } from "@m19-board/guards/dsp-blacklist-save-guard/dsp-blacklist-save.guard";
import { readOnlyGuard, readOnlyGuarded } from "@m19-board/guards/read-only/read-only.guard";
import { DspProductTourOverlayComponent } from "@m19-board/overlay/overlays/product-tour/dsp-product-tour-overlay.component";
import { accountStatusOverlays, dspAccessOverlays } from "@m19-board/overlay/overlays/overlay-lists";
import { DspBidderOffOverlayComponent } from "@m19-board/overlay/overlays/bidder-off/dsp-bidder-off-overlay.component";
import { NoDspAdvertiserLinkedOverlayComponent } from "@m19-board/overlay/overlays/no-dsp-advertiser-linked/no-dsp-advertiser-linked-overlay.component";
import { DspStartFreeTestOverlayComponent } from "@m19-board/overlay/overlays/start-free-test/dsp-start-free-test-overlay.component";
import { AmcStrategyAnalysisCreationPageComponent } from "@m19-board/amc/amc-strategy-analysis-creation-page/amc-strategy-analysis-creation-page.component";

export const DSP_STRATEGIES_URL = "/advertising/dsp/strategies";
export const DSP_BLACKLIST_URL = "/advertising/dsp/blacklist";

export const DspRoutes: M19Route[] = [
  {
    path: "",
    component: DspLayoutComponent,
    providers: [
      {
        provide: StrategyService,
        useExisting: DspStrategyService,
      },
    ],
    data: {
      overlays: [...dspAccessOverlays(), NoDspAdvertiserLinkedOverlayComponent],
    },
    children: [
      {
        path: "stats",
        component: DspStatsComponent,
        data: {
          parentTitle: "page-title.dsp",
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
        },
      },
      {
        path: "audiences",
        data: {
          parentTitle: "page-title.dsp_product_audiences",
          withAccountSelector: true,
          overlays: [DspProductTourOverlayComponent, ...dspAccessOverlays(), NoDspAdvertiserLinkedOverlayComponent],
        },
        children: [
          {
            path: "",
            component: DspAudiencesPageComponent,
          },
          {
            path: "create",
            component: DspCreateAudiencePageComponent,
            ...readOnlyGuarded,
            data: {
              title: "page-title.dsp_create_product_audiences",
              subPage: true,
              parentRouteUrl: "advertising/dsp/audiences",
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
        ],
      },
      {
        path: "strategies",
        data: {
          overlays: [
            DspProductTourOverlayComponent,
            ...dspAccessOverlays(),
            ...accountStatusOverlays({ startFreeTest: DspStartFreeTestOverlayComponent }),
            NoDspAdvertiserLinkedOverlayComponent,
            DspBidderOffOverlayComponent,
          ],
        },
        children: [
          {
            path: "",
            component: DspManagerComponent,
            data: {
              parentTitle: "page-title.dsp_strategies",
              withAccountSelector: true,
              padded: false,
            },
          },
          {
            path: "create",
            component: DspCreateStrategyComponent,
            canActivate: [canCreateDspStrategyGuard, readOnlyGuard],
            runGuardsAndResolvers: readOnlyGuarded.runGuardsAndResolvers,
            data: {
              parentTitle: "page-title.dsp_strategies",
              title: "dsp-create-strategy-page.title",
              parentRouteUrl: DSP_STRATEGIES_URL,
              subPage: true,
              padded: false,
              withChatbotSafeMargin: true,
            },
          },
          {
            path: ":id",
            children: [
              {
                path: "stats/advanced-analysis",
                component: AmcStrategyAnalysisCreationPageComponent,
                ...readOnlyGuarded,
                data: {
                  parentTitle: "page-title.dsp_strategies",
                  parentRouteUrl: DSP_STRATEGIES_URL,
                  subPage: true,
                },
              },
              ...getStrategyPageRoutes(
                CampaignType.DSP,
                "page-title.dsp_strategies",
                DSP_STRATEGIES_URL,
                dspStrategyPageResolver,
              ),
            ],
          },
        ],
      },
      {
        path: "blacklist",
        component: DspBlacklistPageComponent,
        data: {
          parentTitle: "page-title.dsp_strategies",
          title: "page-title.dsp_inventory_blacklist",
          parentRouteUrl: DSP_STRATEGIES_URL,
          subPage: true,
          withAccountSelector: true,
          withDatePicker: true,
          withCurrencySelector: true,
        },
        children: [
          {
            path: "domain",
            component: DspBlacklistComponent,
            data: { inventoryType: DspBlacklistTypeEnum.DOMAIN },
            canDeactivate: [dspBlacklistSaveGuard],
          },
          {
            path: "app",
            component: DspBlacklistComponent,
            data: { inventoryType: DspBlacklistTypeEnum.APP },
            canDeactivate: [dspBlacklistSaveGuard],
          },
          {
            path: "**",
            redirectTo: "domain",
          },
        ],
      },
    ],
  },
  {
    path: "",
    redirectTo: "stats",
    pathMatch: "full",
  },
];
